import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@apollo/react-hooks'
import { toastr } from 'react-redux-toastr'
import { LoaderCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import SnagLinkPicker, { SnagOption } from '@/components/SnagLinkPicker'
import { ATTACH_PHOTOS_TO_SNAG } from '../api'
import { describePhotosError, photosErrorKey } from '../lib/errors'

interface Props {
  projectId: string
  photoIds: string[]
  open: boolean
  onOpenChange: (open: boolean) => void
  onDone?: () => void
}

const AttachToSnagDialog: React.FC<Props> = ({ projectId, photoIds, open, onOpenChange, onDone }) => {
  const { t } = useTranslation()
  const [snag, setSnag] = useState<SnagOption | null>(null)
  const [attach, { loading }] = useMutation(ATTACH_PHOTOS_TO_SNAG, { context: { handlesErrors: true }, refetchQueries: ['photos', 'photoReferences'] })

  const confirm = async () => {
    if (!snag) return
    try {
      await attach({ variables: { snagId: snag.value, photoIds } })
      toastr.success('', t('Photos_attached_to_snag', { count: photoIds.length }))
      onOpenChange(false)
      setSnag(null)
      onDone?.()
    } catch (e: any) {
      toastr.error(t('Common_error'), describePhotosError(photosErrorKey(e), t))
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => {
        if (loading) return
        if (!next) setSnag(null)
        onOpenChange(next)
      }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('Photos_attach_to_snag_title', { count: photoIds.length })}</DialogTitle>
        </DialogHeader>
        <SnagLinkPicker projectId={projectId} value={snag} onChange={setSnag} placeholder={t('Photos_attach_to_snag_pick')} />
        <div className="flex items-center justify-end gap-2 pt-4 border-t">
          <Button variant="outline" disabled={loading} onClick={() => { setSnag(null); onOpenChange(false) }}>{t('Common_cancel')}</Button>
          <Button disabled={loading || !snag} onClick={confirm}>
            {loading && <LoaderCircle className="h-4 w-4 animate-spin" />}
            {t('Photos_attach_to_snag')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default AttachToSnagDialog
